// src/scenes/GuestMenuScene.js
// v3 - Uses startScene helper, UIController for login, correct special access handling

import BaseMenuScene from "./BaseMenuScene.js";
import MenuUIUtils from "../ui/MenuUIUtils.js";
import PPTHeroSceneManager from "./ppt-hero/PPTHeroSceneManager.js";
import { showAuthOverlay } from "../ui/authUI.js";
// Removed showLeaderboardOverlay - now using HallOfFameDesktopTemplate directly
import UIController from "../ui/uiController.js";
import { hasSpecialAccess } from "../services/specialAccessService.js";
import sceneManager from "../utils/SceneManager.js"; // Keep import for safety fallback

class GuestMenuScene extends BaseMenuScene {
    constructor() {
        super({ key: "GuestMenuScene" });
        this.centered = false; // Use grid layout
        this.specialAccessChecked = false;
    }

    create() {
        super.create();
        this.tiles = [];
        this.setRibbonVisibility(true);

        this.createGuestUI();
        this.handleResize();

       
    }

    createSimpleLoadingUI() {
        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        this.uiElements.loadingText = this.add.text(
            width / 2, height / 2,
            "Special access detected - redirecting...",
            { fontFamily: 'monospace', fontSize: '18px', color: '#00FF00' }
        ).setOrigin(0.5);
    }

    // src/scenes/GuestMenuScene.js

    createGuestUI() {
        const topBarY = 40;
        const tileStartY = 100;

        // --- Create Header Buttons (Keep as is) ---
        this.headerButtons = this.createHeaderButtons(
            topBarY,
            [ { key: 'loginButton', text: 'Login', callback: () => { /* ... login logic ... */
                    console.log("Login button clicked in GuestMenuScene");
                    try { if (window.UIController) { window.UIController.setActiveScene(this); } UIController.showOverlay('auth-overlay'); setTimeout(() => { const emailInput = document.getElementById('auth-email'); if (emailInput) emailInput.focus(); }, 100); }
                    catch (e) { console.error("Error showing auth overlay from GuestMenuScene:", e); this.showMessage("Error showing login form", 2000, true); }
                }, color: MenuUIUtils.COLORS.primary, width: 100 } ],
            [ { key: 'guestLabel', text: "Guest", isLabel: true, color: MenuUIUtils.COLORS.textDark } ]
        );
        // --- End Header Buttons ---

        // --- Define Tile Data with 'teamComingSoon' ---
        const tileData = [
            {
                title: "PPT HERO",
                color: MenuUIUtils.COLORS.accent2, // Blue
                solo: async () => await PPTHeroSceneManager.startPPTHero(this, 'generic'), // Guests always get generic
                teamComingSoon: true // <<< Use flag for greyed-out but clickable
            },
            {
                title: "Slide Wars",
                color: MenuUIUtils.COLORS.accent1, // Orange
                soloComingSoon: true, // Grey out solo mode - coming soon
                soloComingSoonMessage: "Slide Wars is still in development - coming soon!",
                teamComingSoon: true, // Grey out team mode - coming soon
                teamComingSoonMessage: "Slide Wars is still in development - coming soon!"
            },
            {
                title: "Shortcut Mastery",
                color: MenuUIUtils.COLORS.warning, // Yellow
                soloComingSoon: true, // Grey out solo mode - coming soon
                soloComingSoonMessage: "Shortcut Mastery is still in development - coming soon!",
                teamComingSoon: true, // Grey out team mode - coming soon
                teamComingSoonMessage: "Shortcut Mastery is still in development - coming soon!"
            },
            {
               title: "Philosophy Flow",
               color: MenuUIUtils.COLORS.accent3, // Purple
               solo: () => this.startScene("PhilosophyFlowScene"),
               teamComingSoon: true, // <<< Use flag
               teamComingSoonMessage: "Philosophy Flow is a solo game." // <<< Custom message
            },
            {
               title: "Classify Tech",
               color: MenuUIUtils.COLORS.accent4, // Lime
               soloComingSoon: true, // Grey out solo mode - coming soon
               soloComingSoonMessage: "Classify Tech is still in development - coming soon!",
               teamComingSoon: true, // Grey out team mode - coming soon
               teamComingSoonMessage: "Classify Tech is still in development - coming soon!"
            },
            {
                title: "User Profile",
                color: MenuUIUtils.COLORS.textMedium, // Grey
                utility: () => this.showMessage("Sign in to view your profile") // Keep guest message
            },
            {
                title: "Hall of Fame",
                color: MenuUIUtils.COLORS.warning, // Yellow
                utility: async () => { /* ... existing utility logic ... */
                    try { 
                        console.log('🏆 Opening new desktop Hall of Fame template from guest menu...');
                        this.startScene('HallOfFameDesktopTemplate'); 
                    }
                    catch (e) { console.error("Error showing leaderboard:", e); this.showMessage("Error loading leaderboard.", 2000, true); }
                }
            }
        ];
        // --- End Tile Data ---

        // Create the grid
        this.createGameTileGrid(tileStartY, tileData, {
            maxTileWidth: 280, tileHeight: 130, horizontalGap: 20, verticalGap: 20, bottomPadding: 80
        });

        // --- Version Text and Access Code (Keep as is) ---
        const version = window.PROJECT_VERSION || '3.1.0';
        this.uiElements.versionText = this.add.text( this.cameras.main.width / 2, this.cameras.main.height - 15, `v${version}`, { fontFamily: 'monospace', fontSize: '12px', color: '#444444' } ).setOrigin(0.5).setInteractive({ useHandCursor: true });
        this.uiElements.versionText.on('pointerover', () => { this.uiElements.versionText.setColor('#777777'); });
        this.uiElements.versionText.on('pointerout', () => { this.uiElements.versionText.setColor('#444444'); });
        this.uiElements.versionText.on('pointerdown', () => { this.showAccessCodeOverlay(); });
        // --- End Version Text ---
    }

    showAccessCodeOverlay() {
        if (window.UIController) {
          window.UIController.showOverlay('access-code-overlay');
          setTimeout(() => { const codeInput = document.getElementById('special-access-code'); if (codeInput) codeInput.focus(); }, 100);
        }
    }

    handleResize(gameSize) {
        super.handleResize(gameSize);
        if (!this.cameras?.main) return;
        const width = this.cameras.main.width;
        const topBarY = 40;

        if (this.headerButtons) {
            if (this.headerButtons.loginButton) {
                this.headerButtons.loginButton.setPosition(this.padding + (this.headerButtons.loginButton.width / 2), topBarY);
            }
            const guestLabelElement = this.uiElements.guestLabel || this.headerButtons.guestLabel;
            if (guestLabelElement) {
                guestLabelElement.setPosition(width - this.padding, topBarY);
            }
        }

        if (this.uiElements.versionText) {
            this.uiElements.versionText.setPosition(this.cameras.main.width / 2, this.cameras.main.height - 15);
        }
    }

    shutdown() {
        console.log(`[${this.scene.key}] Shutting down...`);
        super.shutdown();
        console.log(`[${this.scene.key}] Shutdown complete.`);
    }
}

export default GuestMenuScene;